export type MatchRange = { start: number; end: number };

// `ranges` are sorted and never overlap, so a renderer can bold them in order.
export type NameMatch = { score: number; ranges: MatchRange[] };

const PREFIX_SCORE = 3;
const WORD_START_SCORE = 2;
const MID_WORD_SCORE = 1;
// Words matched apart rank just below the same tier matched as one phrase.
const SPLIT_PENALTY = 0.25;

const isWordStart = (text: string, index: number) =>
  index === 0 || !/[a-z0-9]/i.test(text.charAt(index - 1));

// The best-ranked place `needle` occurs in `haystack`, both already lowercase.
const bestOccurrence = (haystack: string, needle: string) => {
  let best: { score: number; start: number } | null = null;
  for (
    let start = haystack.indexOf(needle);
    start !== -1;
    start = haystack.indexOf(needle, start + 1)
  ) {
    const score =
      start === 0
        ? PREFIX_SCORE
        : isWordStart(haystack, start)
          ? WORD_START_SCORE
          : MID_WORD_SCORE;
    if (!best || score > best.score) {
      best = { score, start };
    }
    if (score === PREFIX_SCORE) {
      break;
    }
  }
  return best;
};

const mergeRanges = (ranges: MatchRange[]): MatchRange[] =>
  [...ranges]
    .sort((a, b) => a.start - b.start)
    .reduce<MatchRange[]>((merged, range) => {
      const last = merged.at(-1);
      if (last && range.start <= last.end) {
        last.end = Math.max(last.end, range.end);
      } else {
        merged.push({ start: range.start, end: range.end });
      }
      return merged;
    }, []);

// A prefix ranks above a word start, which ranks above a match mid-word.
// Every word of the query must appear, in any order: "lapsed buyers" matches "Lapsed 90d buyers".
export const matchName = (name: string, query: string): NameMatch | null => {
  const haystack = name.toLowerCase();
  const phrase = query.trim().toLowerCase().replace(/\s+/g, " ");
  if (!phrase) {
    return null;
  }
  const whole = bestOccurrence(haystack, phrase);
  if (whole) {
    return {
      score: whole.score,
      ranges: [{ start: whole.start, end: whole.start + phrase.length }],
    };
  }
  const tokens = phrase.split(" ");
  if (tokens.length < 2) {
    return null;
  }
  const found = tokens.map((token) => {
    const occurrence = bestOccurrence(haystack, token);
    // A lone letter anywhere matches nearly every name, so it counts only at a word start.
    if (
      !occurrence ||
      (token.length === 1 && occurrence.score < WORD_START_SCORE)
    ) {
      return null;
    }
    return { ...occurrence, end: occurrence.start + token.length };
  });
  if (found.some((occurrence) => !occurrence)) {
    return null;
  }
  const occurrences = found.filter((occurrence) => occurrence !== null);
  return {
    score:
      Math.min(...occurrences.map((occurrence) => occurrence.score)) -
      SPLIT_PENALTY,
    ranges: mergeRanges(occurrences),
  };
};
